import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { View, Text, ScrollView, RefreshControl, Modal } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useFocusEffect } from 'expo-router';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc, useTRPCClient } from '@/lib/trpc';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { isUnauthorizedError } from '@/lib/authUtils';
import ScoreUpdaterStatus from '@/components/ScoreUpdaterStatus';
import { OddsUpdaterStatus } from '@/components/OddsUpdaterStatus';
import { AppSelect } from '@/components/ui/AppSelect';
import {
  Calendar,
  CheckCircle,
  Clock,
  Shield,
  Download,
  Timer,
  RefreshCw,
  Users,
  Bell,
} from 'lucide-react-native';
import AppHeader from '@/components/AppHeader';
import { colors, typography } from '@/lib/theme';
import { notificationPresetOptions } from '@/lib/notificationPresets';

const modalOverlayColor = 'rgba(0, 0, 0, 0.6)';
const restrictedIconStyle = { marginBottom: 16 };
const autoWeekTriggerStyle = { minWidth: 140 };
const fullWidthTriggerStyle = { width: '100%' as const };

interface NflWeek {
  id: string;
  season: number;
  week: number;
  startDate: string;
  endDate: string;
  isActive: boolean | null;
}

type AdminUserSummary = {
  id: string;
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
  createdAt?: string | null;
};

export function WeekManagementRestrictedState() {
  return (
    <View style={styles.screenRoot}>
      <AppHeader title="Week Management" />
      <ScrollView style={styles.screenRoot}>
        <View style={styles.centeredState}>
          <View style={styles.restrictedCopy}>
            <Shield size={48} color={colors.text.tertiary} style={restrictedIconStyle} />
            <Text style={styles.restrictedTitle}>
              Access Restricted
            </Text>
            <Text style={styles.restrictedSubtitle}>
              Only administrators can access the Week Management page.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

export default function WeekManagementScreen() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const trpcClient = useTRPCClient();
  const { user } = useAuth();
  const [refreshing, setRefreshing] = useState(false);
  const [selectedWeekId, setSelectedWeekId] = useState<string | null>(null);
  const [scheduleWeekOverride, setScheduleWeekOverride] = useState<string>('auto');
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [resetModalVisible, setResetModalVisible] = useState(false);
  const [resetTarget, setResetTarget] = useState<AdminUserSummary | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [notificationTarget, setNotificationTarget] = useState<'all' | 'missing'>('all');
  const [notificationPreset, setNotificationPreset] = useState(notificationPresetOptions[0]!.value);
  const [notificationMessage, setNotificationMessage] = useState(notificationPresetOptions[0]!.message);
  const notificationMessageLimit = 200;
  const [notificationTitle, setNotificationTitle] = useState(notificationPresetOptions[0]!.title);
  const notificationTitleLimit = 80;
  const [mmImportLeagueId, setMmImportLeagueId] = useState('');
  const [mmImportSeasonId, setMmImportSeasonId] = useState('');
  const [mmImportSeasonYear, setMmImportSeasonYear] = useState(String(new Date().getFullYear()));
  const [mmImportRosterSize, setMmImportRosterSize] = useState('6');
  const [mmCatalogSeasonYear, setMmCatalogSeasonYear] = useState(String(new Date().getFullYear()));

  // Check if user is admin
  const isAdmin = Boolean(user?.isAdmin);

  useFocusEffect(
    useCallback(() => {
      if (!isAdmin) {
        return;
      }
      // Refresh data when screen comes into focus
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter());
    }, [isAdmin, queryClient])
  );

  const onRefresh = useCallback(async () => {
    if (!isAdmin) return;
    setRefreshing(true);
    await queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
    await queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter());
    setRefreshing(false);
  }, [isAdmin, queryClient]);

  const { data: weeks, isLoading } = useQuery(
    trpc.nfl.weeks.queryOptions(undefined, {
      enabled: isAdmin,
    }),
  );

  const { data: currentWeek } = useQuery(
    trpc.nfl.currentWeek.queryOptions(undefined, {
      enabled: isAdmin,
    }),
  );

  const { data: matchupUpdaterStatus } = useQuery(
    trpc.admin.matchupAnalysisUpdaterStatus.queryOptions(undefined, {
      enabled: isAdmin,
      refetchInterval: (query) => query.state.data?.storyRefresh?.status === 'running' ? 3000 : false,
    }),
  );
  const storyRefresh = matchupUpdaterStatus?.storyRefresh;

  useEffect(() => {
    if (storyRefresh && storyRefresh.status !== 'running') {
      queryClient.invalidateQueries({ queryKey: trpc.nfl.matchupAnalysis.queryKey() });
    }
  }, [queryClient, storyRefresh]);

  const { data: adminLeagues } = useQuery(
    trpc.admin.leagues.queryOptions(undefined, {
      enabled: isAdmin,
    }),
  );

  const { data: adminLeagueSeasons } = useQuery(
    trpc.admin.leagueSeasons.queryOptions(
      { leagueId: mmImportLeagueId },
      {
        enabled: isAdmin && mmImportLeagueId.trim().length > 0,
      },
    ),
  );
  const selectedAdminSeason = (adminLeagueSeasons ?? []).find((season) => season.id === mmImportSeasonId) ?? null;

  const { data: mmAdminStatus, isFetching: isFetchingMmAdminStatus } = useQuery(
    trpc.admin.marchMadnessStatus.queryOptions(
      { leagueId: mmImportLeagueId, seasonId: mmImportSeasonId },
      {
        enabled:
          isAdmin &&
          mmImportLeagueId.trim().length > 0 &&
          mmImportSeasonId.trim().length > 0 &&
          selectedAdminSeason?.mode === 'march-madness-player-pool',
      },
    ),
  );

  useEffect(() => {
    if (!mmImportLeagueId) {
      setMmImportSeasonId('');
      return;
    }
    const seasons = adminLeagueSeasons ?? [];
    if (seasons.length === 0) {
      setMmImportSeasonId('');
      return;
    }
    const selectedExists = seasons.some((season) => season.id === mmImportSeasonId);
    if (selectedExists) return;
    const mmActive =
      seasons.find((season) => season.mode === 'march-madness-player-pool' && season.isActive) ??
      seasons.find((season) => season.mode === 'march-madness-player-pool') ??
      seasons[0];
    setMmImportSeasonId(mmActive?.id ?? '');
  }, [adminLeagueSeasons, mmImportLeagueId, mmImportSeasonId]);

  const selectedWeek = useMemo(
    () => (weeks ?? []).find((week) => week.id === selectedWeekId) ?? null,
    [selectedWeekId, weeks],
  );

  useEffect(() => {
    if (!isAdmin) return;
    const availableWeeks = weeks ?? [];

    if (availableWeeks.length === 0) {
      setSelectedWeekId(null);
      return;
    }

    const fallbackWeekId = availableWeeks[0]?.id;

    if (!fallbackWeekId) {
      setSelectedWeekId(null);
      return;
    }

    const selectionExists = availableWeeks.some((week) => week.id === selectedWeekId);

    if (!selectedWeekId || !selectionExists) {
      const activeWeek = availableWeeks.find((week) => week.isActive);
      setSelectedWeekId(activeWeek?.id ?? fallbackWeekId);
    }
  }, [isAdmin, selectedWeekId, weeks]);

  const activateWeekMutation = useMutation({
    mutationFn: (weekId: string) => trpcClient.nfl.activateWeek.mutate({ weekId }),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter());
      toast.show({
        title: "Week Updated",
        description: "Active week has been updated successfully.",
      });
    },
    onError: (_error) => {
      toast.show({
        title: "Error",
        description: "Failed to update active week. Please try again.",
        variant: "destructive",
      });
    },
  });

  const populateScheduleMutation = useMutation({
    mutationFn: () => trpcClient.nfl.populateSchedule.mutate({}),
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.nfl.currentWeek.queryFilter());
      toast.show({
        title: "Schedule Updated",
        description: `Created ${data.weeksUpserted} weeks and ${data.gamesCreated} games from ${data.totalEvents} events`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to populate schedule. Please try again.",
        variant: "destructive",
      });
    },
  });

  const refreshScheduleMutation = useMutation({
    mutationFn: async () => {
      const payload =
        scheduleWeekOverride === 'auto'
          ? {}
          : { weekOverride: Number(scheduleWeekOverride) };
      return trpcClient.nfl.refreshSchedule.mutate(payload);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      toast.show({
        title: "Schedule Refreshed",
        description: data.refreshed ? "Latest game times have been pulled from API-NFL." : "Schedule refresh was skipped.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to refresh schedule. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateOddsMutation = useMutation({
    mutationFn: () => trpcClient.admin.triggerOddsUpdater.mutate(),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());

      if (data.skipped) {
        toast.show({
          title: "Odds Update Skipped",
          description: "An update may already be running, or there are no upcoming games.",
        });
        return;
      }

      if (!data.complete) {
        toast.show({
          title: "Odds Update Incomplete",
          description: "One or more odds sources failed. Check the updater status and try again.",
          variant: "destructive",
        });
        return;
      }

      toast.show({
        title: "Odds Updated",
        description: "Updated spreads, totals, and moneylines for upcoming games.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to update odds. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateScoresApiNflMutation = useMutation({
    mutationFn: () => trpcClient.nfl.updateScores.mutate(),
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      toast.show({
        title: "API-NFL Scores Updated",
        description: `Updated ${data.gamesUpdated} games, ${data.gamesCompleted} newly completed`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to update scores from API-NFL. Please try again.",
        variant: "destructive",
      });
    },
  });

  const updateScoresMutation = useMutation({
    mutationFn: () => trpcClient.nfl.updateScores.mutate(),
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.nfl.weeks.queryFilter());
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      toast.show({
        title: "Odds API Scores Updated",
        description: `Updated ${data.gamesUpdated} games, ${data.gamesCompleted} newly completed`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to update scores from Odds API. Please try again.",
        variant: "destructive",
      });
    },
  });

  const usersQuery = useInfiniteQuery({
    queryKey: ['admin-users', userSearch],
    enabled: isAdmin,
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      trpcClient.admin.users.query({
        limit: 25,
        offset: pageParam,
        ...(userSearch ? { search: userSearch } : {}),
      }),
    getNextPageParam: (lastPage) => {
      const offset = lastPage?.offset ?? 0;
      const fetched = lastPage?.users?.length ?? 0;
      const total = lastPage?.total ?? 0;
      const nextOffset = offset + fetched;
      return nextOffset < total ? nextOffset : undefined;
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (payload: { userId: string; email?: string; newPassword: string }) =>
      trpcClient.admin.resetPassword.mutate(payload),
    onSuccess: () => {
      toast.show({
        title: 'Password Updated',
        description: 'The password was reset and the user has been signed out.',
      });
      setResetModalVisible(false);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      const message = error instanceof Error ? error.message : 'Failed to reset password';
      toast.show({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const users = useMemo(
    () => usersQuery.data?.pages.flatMap((page) => page.users) ?? [],
    [usersQuery.data]
  );

  const testPushMutation = useMutation({
    mutationFn: () => trpcClient.admin.testPush.mutate(),
    onSuccess: () => {
      toast.show({
        title: "Test Sent",
        description: "Notification sent to your device.",
      });
    },
    onError: (error) => {
      toast.show({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to send test push",
        variant: "destructive",
      });
    },
  });

  const remindUsersMutation = useMutation({
    mutationFn: () => trpcClient.admin.remindUsers.mutate(),
    onSuccess: (data) => {
      toast.show({
        title: "Reminders Sent",
        description: data.message ?? `Sent reminders to ${data.count} users.`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to send reminders. Please try again.",
        variant: "destructive",
      });
    },
  });

  const sendGlobalNotificationMutation = useMutation({
    mutationFn: async () => {
      const trimmedMessage = notificationMessage.trim();
      const trimmedTitle = notificationTitle.trim() || 'League Update';

      if (notificationTarget === 'missing' && !selectedWeekId) {
        throw new Error('Select a week to remind users with missing picks.');
      }

      return trpcClient.admin.sendNotifications.mutate({
        target: notificationTarget,
        message: trimmedMessage,
        title: trimmedTitle,
        ...(notificationTarget === 'missing' && selectedWeekId ? { weekId: selectedWeekId } : {}),
      });
    },
    onSuccess: (data) => {
      toast.show({
        title: 'Notifications Sent',
        description: data?.message ?? 'Your league members have been notified.',
      });
    },
    onError: (error) => {
      const message = error instanceof Error ? error.message : 'Failed to send notifications';
      toast.show({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    },
  });

  const triggerAutoPicksMutation = useMutation({
    mutationFn: () => trpcClient.admin.triggerAutoPicks.mutate(),
    onSuccess: (data) => {
      queryClient.invalidateQueries(trpc.leagues.list.queryFilter());
      toast.show({
        title: "Auto Picks Assigned",
        description: data.message ?? "Auto picks were assigned to eligible users.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to assign auto picks. Please try again.",
        variant: "destructive",
      });
    },
  });

  const triggerWorldCupSyncMutation = useMutation({
    mutationFn: () => trpcClient.admin.triggerWorldCupSync.mutate(),
    onSuccess: (data) => {
      toast.show({
        title: "World Cup Sync Triggered",
        description: data?.message ?? "World Cup sync run has been started.",
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to trigger World Cup sync. Please try again.",
        variant: "destructive",
      });
    },
  });

  const refreshMatchupAnalysisMutation = useMutation({
    mutationFn: () => trpcClient.admin.triggerMatchupAnalysisUpdater.mutate(),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: trpc.nfl.matchupAnalysis.queryKey() });
      queryClient.invalidateQueries(trpc.admin.matchupAnalysisUpdaterStatus.queryFilter());
      const blocked = Boolean(data.storyRefresh.providerError) || data.storyRefresh.status === 'failed';
      toast.show({
        title: blocked
          ? "Scouting Reports Refresh Blocked"
          : data.storyRefresh.status === 'running'
            ? "Scouting Reports Refreshing"
            : "Scouting Reports Refresh Complete",
        description: data.message,
        variant: blocked ? "destructive" : undefined,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: "Unauthorized",
          description: "You are logged out. Please log in again.",
          variant: "destructive",
        });
        return;
      }
      toast.show({
        title: "Error",
        description: "Failed to refresh matchup analysis. Please try again.",
        variant: "destructive",
      });
    },
  });

  const importMarchMadnessMutation = useMutation({
    mutationFn: async () => {
      const leagueId = mmImportLeagueId.trim();
      const seasonId = mmImportSeasonId.trim();
      const seasonYear = Number.parseInt(mmImportSeasonYear.trim(), 10);
      const rosterSize = Number.parseInt(mmImportRosterSize.trim(), 10);

      if (!leagueId || !seasonId) {
        throw new Error('League ID and Season ID are required.');
      }
      if (!Number.isInteger(seasonYear) || seasonYear < 2000 || seasonYear > 2200) {
        throw new Error('Enter a valid season year (e.g. 2026).');
      }

      return trpcClient.admin.marchMadnessImport.mutate({
        leagueId,
        seasonId,
        seasonYear,
        gender: 'mens',
        rosterSize: Number.isInteger(rosterSize) && rosterSize > 0 ? rosterSize : 6,
      });
    },
    onSuccess: (data: any) => {
      toast.show({
        title: 'March Madness Import Complete',
        description: `Teams: ${data?.teamsImported ?? 0}, Players: ${data?.playersImported ?? 0}, Games: ${data?.gamesImported ?? 0}`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Import Failed',
        description: error instanceof Error ? error.message : 'Failed to import March Madness data',
        variant: 'destructive',
      });
    },
  });

  const loadMarchMadnessCatalogMutation = useMutation({
    mutationFn: async () => {
      const seasonYear = Number.parseInt(mmCatalogSeasonYear.trim(), 10);
      if (!Number.isInteger(seasonYear) || seasonYear < 2000 || seasonYear > 2200) {
        throw new Error('Enter a valid season year (e.g. 2026).');
      }

      return trpcClient.admin.marchMadnessCatalogLoad.mutate({
        seasonYear,
        gender: 'mens',
      });
    },
    onSuccess: (data: any) => {
      toast.show({
        title: 'Global Player Catalog Loaded',
        description: `Teams: ${data?.teamsImported ?? 0}, Players: ${data?.playersImported ?? 0}, Games: ${data?.gamesImported ?? 0}`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Catalog Load Failed',
        description: error instanceof Error ? error.message : 'Failed to load global player catalog',
        variant: 'destructive',
      });
    },
  });

  const syncMarchMadnessSeasonMutation = useMutation({
    mutationFn: async () => {
      const leagueId = mmImportLeagueId.trim();
      const seasonId = mmImportSeasonId.trim();
      if (!leagueId || !seasonId) {
        throw new Error('League ID and Season ID are required.');
      }

      return trpcClient.admin.marchMadnessSync.mutate({ leagueId, seasonId });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries(
        trpc.admin.marchMadnessStatus.queryFilter({
          leagueId: mmImportLeagueId,
          seasonId: mmImportSeasonId,
        }),
      );
      toast.show({
        title: 'March Madness Sync Complete',
        description: data?.degraded
          ? `Degraded sync: ${data?.failedGameIds?.length ?? 0} failed games, ${data?.gamesWithoutStats?.length ?? 0} games without stats`
          : `Synced ${data?.gameBoxScoresSynced ?? 0} games and ${data?.statsRowsUpserted ?? 0} stat rows`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Season Sync Failed',
        description: error instanceof Error ? error.message : 'Failed to sync March Madness season',
        variant: 'destructive',
      });
    },
  });

  const recomputeMarchMadnessSeasonMutation = useMutation({
    mutationFn: async () => {
      const leagueId = mmImportLeagueId.trim();
      const seasonId = mmImportSeasonId.trim();
      if (!leagueId || !seasonId) {
        throw new Error('League ID and Season ID are required.');
      }

      return trpcClient.admin.marchMadnessRecompute.mutate({ leagueId, seasonId });
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries(
        trpc.admin.marchMadnessStatus.queryFilter({
          leagueId: mmImportLeagueId,
          seasonId: mmImportSeasonId,
        }),
      );
      toast.show({
        title: 'Scores Recomputed',
        description: `Rounds recomputed: ${(data?.roundsRecomputed ?? []).join(', ') || 'none'}`,
      });
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast.show({
          title: 'Unauthorized',
          description: 'You are logged out. Please log in again.',
          variant: 'destructive',
        });
        return;
      }
      toast.show({
        title: 'Recompute Failed',
        description: error instanceof Error ? error.message : 'Failed to recompute March Madness season scores',
        variant: 'destructive',
      });
    },
  });

  const adminLeagueOptions = useMemo(
    () =>
      (adminLeagues ?? []).map((league) => ({
        value: league.id,
        label: league.name,
      })),
    [adminLeagues],
  );
  const adminSeasonOptions = useMemo(
    () =>
      (adminLeagueSeasons ?? []).map((season) => ({
        value: season.id,
        label:
          season.mode === 'march-madness-player-pool'
            ? `${season.label} (MM${season.isActive ? ', active' : ''})`
            : `${season.label}${season.isActive ? ' (active)' : ''}`,
      })),
    [adminLeagueSeasons],
  );

  const mmImportMetadata = (mmAdminStatus?.importMetadata ?? {}) as Record<string, unknown>;
  const mmLastSyncFailedGameIds = Array.isArray(mmImportMetadata.lastSyncFailedGameIds)
    ? (mmImportMetadata.lastSyncFailedGameIds as string[])
    : [];
  const mmLastSyncGamesWithoutStats = Array.isArray(mmImportMetadata.lastSyncGamesWithoutStats)
    ? (mmImportMetadata.lastSyncGamesWithoutStats as string[])
    : [];
  const mmLastSyncMissingPlayerIds = Array.isArray(mmImportMetadata.lastSyncMissingPlayerSourceIdsSample)
    ? (mmImportMetadata.lastSyncMissingPlayerSourceIdsSample as string[])
    : [];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  const notificationRecipientOptions = useMemo(
    () => [
      { value: 'all', label: 'All league members' },
      { value: 'missing', label: 'Missing picks (selected week)' },
    ],
    [],
  );

  const formatRelativeKickoff = (date: Date) => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const startOfDayAfterTomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 2);

    if (date >= startOfToday && date < startOfTomorrow) {
      return 'later today';
    }
    if (date >= startOfTomorrow && date < startOfDayAfterTomorrow) {
      return 'tomorrow';
    }
    return 'soon';
  };

  const lastCallMessage = useMemo(() => {
    if (!selectedWeek?.startDate) {
      return 'Last call! Picks lock soon for this week.';
    }
    const kickoffTime = new Date(selectedWeek.startDate);
    if (isNaN(kickoffTime.getTime())) {
      return 'Last call! Picks lock soon for this week.';
    }
    return `Picks lock ${formatRelativeKickoff(kickoffTime)}, be sure to get your picks in soon!`;
  }, [selectedWeek?.startDate]);
  const lastCallMessageRef = useRef(lastCallMessage);

  const handleNotificationPresetChange = (value: string) => {
    setNotificationPreset(value);
    if (value === 'last-call') {
      setNotificationMessage(lastCallMessage);
      setNotificationTitle(
        notificationPresetOptions.find((option) => option.value === value)?.title ??
          'League Update',
      );
      return;
    }
    const preset = notificationPresetOptions.find((option) => option.value === value);
    if (preset) {
      setNotificationMessage(preset.message);
    }
    if (preset?.title) {
      setNotificationTitle(preset.title);
    }
  };

  useEffect(() => {
    const previousMessage = lastCallMessageRef.current;
    if (notificationPreset === 'last-call' && notificationMessage === previousMessage) {
      setNotificationMessage(lastCallMessage);
    }
    lastCallMessageRef.current = lastCallMessage;
  }, [lastCallMessage, notificationMessage, notificationPreset]);

  const getWeekStatus = (week: NflWeek) => {
    const now = new Date();
    const startDate = new Date(week.startDate);
    const endDate = new Date(week.endDate);

    if (now < startDate) {
      return { status: 'upcoming', variant: 'outline' as const };
    } else if (now > endDate) {
      return { status: 'completed', variant: 'secondary' as const };
    } else {
      return { status: 'current', variant: 'default' as const };
    }
  };

  const selectedWeekStatus = selectedWeek ? getWeekStatus(selectedWeek) : null;
  const isSelectedWeekActive = selectedWeekId === currentWeek?.id;
  const canSetActiveWeek = Boolean(selectedWeekId && !isSelectedWeekActive && !activateWeekMutation.isPending);
  const scheduleWeekOptions = weeks?.length
    ? Array.from(new Set(weeks.map((week) => week.week))).sort((a, b) => a - b)
    : Array.from({ length: 18 }, (_, index) => index + 1);
  const userCount = usersQuery.data?.pages?.[0]?.total ?? 0;

  const openResetModal = (target: AdminUserSummary) => {
    setResetTarget(target);
    setResetPassword('');
    setResetConfirm('');
    setResetError(null);
    setResetModalVisible(true);
  };

  const closeResetModal = () => {
    if (resetPasswordMutation.isPending) return;
    setResetModalVisible(false);
    setResetTarget(null);
    setResetPassword('');
    setResetConfirm('');
    setResetError(null);
  };

  const handleResetPassword = () => {
    if (!resetTarget) {
      setResetError('Select a user to reset.');
      return;
    }
    if (!resetPassword.trim()) {
      setResetError('Enter a new password.');
      return;
    }
    if (resetPassword.trim() !== resetConfirm.trim()) {
      setResetError('Passwords do not match.');
      return;
    }
    setResetError(null);
    resetPasswordMutation.mutate({
      userId: resetTarget.id,
      ...(resetTarget.email ? { email: resetTarget.email } : {}),
      newPassword: resetPassword.trim(),
    });
  };

  if (!isAdmin) {
    return <WeekManagementRestrictedState />;
  }

  if (isLoading) {
    return (
      <View style={styles.screenRoot}>
        <AppHeader title="Week Management" />
        <ScrollView
          style={styles.screenRoot}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
        >
          <View style={styles.screenPadding}>
            {[...Array(6)].map((_, i) => (
              <View
                key={i}
                style={styles.loadingSkeletonCard}
              />
            ))}
          </View>
        </ScrollView>
      </View>
    );
  }

  return (
    <View style={styles.screenRoot}>
      <AppHeader title="Week Management" />
      <ScrollView
        style={styles.screenSurface}
        contentContainerStyle={styles.screenScrollContent}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />}
      >
        <View style={styles.pageContainer}>
          {/* Header */}
          <View style={styles.sectionGap}>
            <View style={styles.topHeaderRow}>
              <View style={styles.flexOne}>
                <Text style={styles.pageTitle}>
                  Week Management
                </Text>
                <Text style={styles.bodyMuted}>
                Manage the active NFL week for your leagues.
              </Text>
            </View>
          </View>

          <Button
            variant="outline"
            onPress={() => populateScheduleMutation.mutate()}
            disabled={populateScheduleMutation.isPending}
            style={styles.selfStart}
          >
            <Download size={16} color={colors.text.secondary} />
            <Text style={styles.textForeground}>
              {populateScheduleMutation.isPending ? "Updating..." : "Update Schedule"}
            </Text>
          </Button>
        </View>

        {/* Admin Data Update Operations */}
        <Card style={styles.cardMargin}>
          <CardHeader>
            <CardTitle>
              <View style={styles.iconRow}>
                <Timer size={20} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>
                  Data Updates
                </Text>
              </View>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text style={styles.descriptionText}>
              Manually update betting odds, refresh game scores, rebuild the current week{"'"}s
              matchup analysis and scouting notes, and assign auto picks to members who miss
              their deadlines. API-NFL provides faster, more frequent updates than Odds API.
            </Text>
            <View style={styles.actionButtonsWrap}>
              <Button
                onPress={() => updateOddsMutation.mutate()}
                disabled={updateOddsMutation.isPending}
                variant="outline"
                style={styles.minActionButton}
              >
                <RefreshCw size={16} color={colors.text.secondary} />
                <Text style={styles.textForeground}>
                  {updateOddsMutation.isPending ? "Updating..." : "Update Betting Odds"}
                </Text>
              </Button>
              <Button
                onPress={() => updateScoresApiNflMutation.mutate()}
                disabled={updateScoresApiNflMutation.isPending}
                style={styles.minPrimaryActionButton}
              >
                <RefreshCw size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {updateScoresApiNflMutation.isPending ? "Updating..." : "Update Scores (API-NFL)"}
                </Text>
              </Button>
              <Button
                onPress={() => updateScoresMutation.mutate()}
                disabled={updateScoresMutation.isPending}
                variant="outline"
                style={styles.minActionButton}
              >
                <RefreshCw size={16} color={colors.text.secondary} />
                <Text style={styles.textForeground}>
                  {updateScoresMutation.isPending ? "Updating..." : "Update Scores (Odds API)"}
                </Text>
              </Button>
              <Button
                onPress={() => populateScheduleMutation.mutate()}
                disabled={populateScheduleMutation.isPending}
                style={styles.minPrimaryActionButton}
              >
                <Download size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {populateScheduleMutation.isPending ? "Populating..." : "Populate NFL Schedule"}
                </Text>
              </Button>
              <Button
                testID="refresh-week-analysis-button"
                accessibilityLabel="Refresh current week analysis and scouting reports"
                onPress={() => refreshMatchupAnalysisMutation.mutate()}
                disabled={refreshMatchupAnalysisMutation.isPending || storyRefresh?.status === 'running'}
                style={styles.minPrimaryActionButton}
              >
                <RefreshCw size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {refreshMatchupAnalysisMutation.isPending || storyRefresh?.status === 'running' ? "Refreshing..." : "Refresh Scouting Reports"}
                </Text>
              </Button>
              {storyRefresh && (
                <Text testID="scouting-report-refresh-status" accessibilityLiveRegion="polite" style={styles.statusFooterText}>
                  {storyRefresh.message}
                </Text>
              )}
              <View style={styles.scheduleActionWrap}>
                <AppSelect
                  value={scheduleWeekOverride}
                  onChange={(value) => setScheduleWeekOverride(value)}
                  options={[
                    { value: 'auto', label: 'Week (auto)' },
                    ...scheduleWeekOptions.map((week) => ({
                      value: String(week),
                      label: `Week ${week}`,
                    })),
                  ]}
                  placeholder="Week (auto)"
                  triggerStyle={autoWeekTriggerStyle}
                />
                <Button
                  onPress={() => refreshScheduleMutation.mutate()}
                  disabled={refreshScheduleMutation.isPending}
                  variant="outline"
                  style={[styles.minActionButton, styles.scheduleRefreshButton]}
                >
                  <RefreshCw size={16} color={colors.text.secondary} />
                  <Text style={styles.textForeground}>
                    {refreshScheduleMutation.isPending ? "Refreshing..." : "Refresh Schedule Times"}
                  </Text>
                </Button>
              </View>
              <Button
                onPress={() => testPushMutation.mutate()}
                disabled={testPushMutation.isPending}
                style={styles.minCyanActionButton}
              >
                <Bell size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {testPushMutation.isPending ? "Sending..." : "Test Push (Me)"}
                </Text>
              </Button>
              <Button
                onPress={() => remindUsersMutation.mutate()}
                disabled={remindUsersMutation.isPending}
                style={styles.minAmberActionButton}
              >
                <Bell size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {remindUsersMutation.isPending ? "Sending..." : "Remind Users"}
                </Text>
              </Button>
              <Button
                onPress={() => triggerAutoPicksMutation.mutate()}
                disabled={triggerAutoPicksMutation.isPending}
                style={styles.minVioletActionButton}
              >
                <Users size={16} color={colors.text.primary} />
                <Text style={styles.textForeground}>
                  {triggerAutoPicksMutation.isPending ? "Assigning..." : "Assign Auto Picks"}
                </Text>
              </Button>
              <Button
                onPress={() => triggerWorldCupSyncMutation.mutate()}
                disabled={triggerWorldCupSyncMutation.isPending}
                variant="outline"
                style={styles.minActionButton}
              >
                <RefreshCw size={16} color={colors.text.secondary} />
                <Text style={styles.textForeground}>
                  {triggerWorldCupSyncMutation.isPending ? "Syncing..." : "Sync World Cup Now"}
                </Text>
              </Button>
            </View>
          </CardContent>
        </Card>

        <Card style={styles.cardMargin}>
          <CardHeader>
            <CardTitle>
              <View style={styles.iconRow}>
                <Download size={20} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>
                  March Madness Import
                </Text>
              </View>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text style={styles.cardDescriptionCompact}>
              Load the global tournament catalog first, then import it into a specific league season.
            </Text>
            <View style={styles.panelCard}>
              <Text style={styles.sectionLabel}>Global Catalog</Text>
              <View style={styles.inlineEndRow}>
                <View style={styles.width160}>
                  <Input
                    value={mmCatalogSeasonYear}
                    onChangeText={setMmCatalogSeasonYear}
                    placeholder="Season Year"
                    keyboardType="number-pad"
                  />
                </View>
                <Button
                  onPress={() => loadMarchMadnessCatalogMutation.mutate()}
                  disabled={loadMarchMadnessCatalogMutation.isPending}
                  style={styles.selfStart}
                >
                  <Download size={16} color={colors.text.primary} />
                  <Text style={styles.textForeground}>
                    {loadMarchMadnessCatalogMutation.isPending ? 'Loading...' : 'Load Global Players'}
                  </Text>
                </Button>
              </View>
            </View>
            <View style={styles.compactSection}>
              <Text style={styles.sectionLabel}>League Season Setup</Text>
              <AppSelect
                value={mmImportLeagueId}
                onChange={(value) => setMmImportLeagueId(value)}
                options={adminLeagueOptions}
                placeholder="Select league"
              />
              <AppSelect
                value={mmImportSeasonId}
                onChange={(value) => setMmImportSeasonId(value)}
                options={adminSeasonOptions}
                placeholder={mmImportLeagueId ? 'Select season' : 'Select league first'}
                disabled={!mmImportLeagueId || adminSeasonOptions.length === 0}
              />
              <View style={styles.rowGap}>
                <View style={styles.flexOne}>
                  <Input
                    value={mmImportSeasonYear}
                    onChangeText={setMmImportSeasonYear}
                    placeholder="Season Year"
                    keyboardType="number-pad"
                  />
                </View>
                <View style={styles.width112}>
                  <Input
                    value={mmImportRosterSize}
                    onChangeText={setMmImportRosterSize}
                    placeholder="Roster"
                    keyboardType="number-pad"
                  />
                </View>
              </View>
            </View>
            <Button
              onPress={() => importMarchMadnessMutation.mutate()}
              disabled={importMarchMadnessMutation.isPending}
              style={styles.selfStart}
            >
              <Download size={16} color={colors.text.primary} />
              <Text style={styles.textForeground}>
                {importMarchMadnessMutation.isPending ? 'Importing...' : 'Import March Madness Data'}
              </Text>
            </Button>

            <View style={styles.panelCardWithTopMargin}>
              <View style={styles.betweenRow}>
                <Text style={styles.sectionLabel}>Season Diagnostics</Text>
                <Badge variant="outline">
                  <Text>
                    {isFetchingMmAdminStatus
                      ? 'Loading'
                      : mmAdminStatus?.importStatus ?? (mmImportSeasonId ? 'Unavailable' : 'Select season')}
                  </Text>
                </Badge>
              </View>

              {mmAdminStatus?.hasSetup ? (
                <>
                  <View style={styles.inlineActionWrap}>
                    <Badge variant="secondary"><Text>{`Teams ${mmAdminStatus.teamsCount}`}</Text></Badge>
                    <Badge variant="secondary"><Text>{`Players ${mmAdminStatus.playersCount}`}</Text></Badge>
                    <Badge variant="secondary"><Text>{`Games ${mmAdminStatus.gamesCount}`}</Text></Badge>
                    <Badge variant="secondary"><Text>{`Stats ${mmAdminStatus.statsCount}`}</Text></Badge>
                    {mmAdminStatus.draft ? (
                      <Badge variant="secondary"><Text>{`Draft ${mmAdminStatus.draft.status}`}</Text></Badge>
                    ) : null}
                  </View>

                  <Text style={styles.captionMutedLine}>
                    {`Roster source: ${String(mmImportMetadata.rosterSource ?? 'unknown')} · Last sync: ${String(mmImportMetadata.lastSyncAt ?? 'never')}`}
                  </Text>

                  {Boolean(mmImportMetadata.lastSyncDegraded) ? (
                    <View style={styles.warningPanel}>
                      <Text style={styles.warningTitle}>
                        Last Sync Degraded
                      </Text>
                      <Text style={styles.warningText}>
                        {`Failed games: ${mmLastSyncFailedGameIds.length} · Empty games: ${mmLastSyncGamesWithoutStats.length} · Missing player stats: ${Number(mmImportMetadata.lastSyncMissingPlayerStatCount ?? 0)}`}
                      </Text>
                      {mmLastSyncFailedGameIds.length > 0 ? (
                        <Text style={styles.warningText}>
                          {`Failed game IDs: ${mmLastSyncFailedGameIds.slice(0, 8).join(', ')}`}
                        </Text>
                      ) : null}
                      {mmLastSyncMissingPlayerIds.length > 0 ? (
                        <Text style={styles.warningText}>
                          {`Missing player IDs: ${mmLastSyncMissingPlayerIds.slice(0, 6).join(', ')}`}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}

                  <View style={styles.actionButtonsWrap}>
                    <Button
                      onPress={() => syncMarchMadnessSeasonMutation.mutate()}
                      disabled={
                        syncMarchMadnessSeasonMutation.isPending ||
                        !mmImportLeagueId ||
                        !mmImportSeasonId ||
                        selectedAdminSeason?.mode !== 'march-madness-player-pool'
                      }
                      variant="outline"
                    >
                      <RefreshCw size={16} color={colors.text.primary} />
                      <Text>
                        {syncMarchMadnessSeasonMutation.isPending ? 'Syncing...' : 'Re-sync Season'}
                      </Text>
                    </Button>
                    <Button
                      onPress={() => recomputeMarchMadnessSeasonMutation.mutate()}
                      disabled={
                        recomputeMarchMadnessSeasonMutation.isPending ||
                        !mmImportLeagueId ||
                        !mmImportSeasonId ||
                        selectedAdminSeason?.mode !== 'march-madness-player-pool'
                      }
                      variant="outline"
                    >
                      <RefreshCw size={16} color={colors.text.primary} />
                      <Text>
                        {recomputeMarchMadnessSeasonMutation.isPending ? 'Recomputing...' : 'Recompute Scores'}
                      </Text>
                    </Button>
                  </View>

                  {Array.isArray(mmImportMetadata.lastRecomputeRounds) &&
                  (mmImportMetadata.lastRecomputeRounds as unknown[]).length > 0 ? (
                    <Text style={styles.statusFooterText}>
                      {`Last recompute: ${String(mmImportMetadata.lastRecomputeAt ?? 'unknown')} · ${(mmImportMetadata.lastRecomputeRounds as string[]).join(', ')}`}
                    </Text>
                  ) : null}
                </>
              ) : (
                <Text style={styles.statusFooterText}>
                  {selectedAdminSeason?.mode && selectedAdminSeason.mode !== 'march-madness-player-pool'
                    ? 'The selected season is not a March Madness season.'
                    : mmImportSeasonId
                      ? 'This season has not been set up for March Madness yet.'
                      : 'Select a league season to inspect diagnostics.'}
                </Text>
              )}
            </View>
          </CardContent>
        </Card>

        {/* Global Push Notifications */}
        <Card style={styles.cardMargin}>
          <CardHeader>
            <CardTitle>
              <View style={styles.iconRow}>
                <Bell size={20} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>
                  Push Notifications (All Leagues)
                </Text>
              </View>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text style={styles.descriptionText}>
              Send a broadcast notification to every league, or target members who still have picks
              missing for the selected NFL week.
            </Text>

            <View style={styles.metaRow}>
              <Text style={styles.sectionLabel}>Audience</Text>
              <Badge variant="outline">
                <Text>{notificationTarget === 'missing' ? 'Targeted' : 'All members'}</Text>
              </Badge>
            </View>

            <View style={styles.fieldGroup}>
              <View>
                <Text style={styles.uppercaseFieldLabel}>
                  Recipients
                </Text>
                <AppSelect
                  value={notificationTarget}
                  onChange={(value) =>
                    setNotificationTarget(value === 'missing' ? 'missing' : 'all')
                  }
                  options={notificationRecipientOptions}
                  placeholder="Choose recipients..."
                />
              </View>

              <View>
                <Text style={styles.uppercaseFieldLabel}>
                  Message preset
                </Text>
                <AppSelect
                  value={notificationPreset}
                  onChange={handleNotificationPresetChange}
                  options={notificationPresetOptions.map((option) => ({
                    value: option.value,
                    label: option.label,
                  }))}
                  placeholder="Choose a preset..."
                />
              </View>

              <View>
                <View style={styles.labelCounterRow}>
                  <Text style={styles.uppercaseFieldLabelTight}>
                    Title
                  </Text>
                  <Text style={styles.counterText}>
                    {notificationTitle.length}/{notificationTitleLimit}
                  </Text>
                </View>
                <Input
                  value={notificationTitle}
                  onChangeText={setNotificationTitle}
                  placeholder="League Update"
                  maxLength={notificationTitleLimit}
                />
              </View>

              <View>
                <View style={styles.labelCounterRow}>
                  <Text style={styles.uppercaseFieldLabelTight}>
                    Message
                  </Text>
                  <Text style={styles.counterText}>
                    {notificationMessage.length}/{notificationMessageLimit}
                  </Text>
                </View>
                <Textarea
                  value={notificationMessage}
                  onChangeText={setNotificationMessage}
                  placeholder="Write your message..."
                  maxLength={notificationMessageLimit}
                />
              </View>
            </View>

            <Text style={styles.broadcastHelpText}>
              {notificationTarget === 'missing'
                ? selectedWeek
                  ? `Targets members missing picks for Week ${selectedWeek.week}.`
                  : 'Select a week above before sending a missing-picks reminder.'
                : 'Sends to all league members with push notifications enabled.'}
            </Text>

            <Button
              onPress={() => sendGlobalNotificationMutation.mutate()}
              disabled={
                !notificationMessage.trim() ||
                (notificationTarget === 'missing' && !selectedWeekId) ||
                sendGlobalNotificationMutation.isPending
              }
              style={styles.primaryBlueButton}
            >
              <Bell size={16} color={colors.text.primary} />
              <Text style={styles.textForeground}>
                {sendGlobalNotificationMutation.isPending ? 'Sending...' : 'Send Notification'}
              </Text>
            </Button>
          </CardContent>
        </Card>

        {/* User Password Reset */}
        <Card style={styles.cardMargin}>
          <CardHeader>
            <CardTitle>
              <View style={styles.iconRow}>
                <Shield size={20} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>
                  User Password Reset
                </Text>
              </View>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text style={styles.descriptionText}>
              Search for a user and set a new password. Resetting a password signs the user out of all sessions.
            </Text>
            <View style={styles.userSearchRow}>
              <View style={styles.userSearchInputWrap}>
                <Input
                  value={userSearchInput}
                  onChangeText={setUserSearchInput}
                  placeholder="Search by email or name"
                  editable={!usersQuery.isFetching}
                />
              </View>
              <Button
                variant="outline"
                onPress={() => {
                  setUserSearch(userSearchInput.trim());
                }}
                disabled={usersQuery.isFetching}
              >
                <Text style={styles.textForeground}>Search</Text>
              </Button>
              <Button
                variant="outline"
                onPress={() => {
                  setUserSearchInput('');
                  setUserSearch('');
                }}
                disabled={usersQuery.isFetching && !userSearch}
              >
                <Text style={styles.textForeground}>Clear</Text>
              </Button>
            </View>

            {usersQuery.isFetching && users.length === 0 ? (
              <Text style={styles.smallMutedText}>Loading users...</Text>
            ) : null}

            {users.length === 0 && !usersQuery.isFetching ? (
              <Text style={styles.smallMutedText}>No users found.</Text>
            ) : null}

            {users.length > 0 ? (
              <View style={styles.userList}>
                <Text style={styles.userListCount}>
                  Showing {users.length} of {userCount}
                </Text>
                {users.map((item) => {
                  const displayName = [item.firstName, item.lastName].filter(Boolean).join(' ');
                  return (
                    <View
                      key={item.id}
                      style={styles.userRow}
                    >
                      <View style={styles.flexOne}>
                        <Text style={styles.userNameText}>
                          {displayName || 'Unnamed User'}
                        </Text>
                        <Text style={styles.statusFooterText}>{item.email}</Text>
                      </View>
                      <Button
                        variant="outline"
                        onPress={() => openResetModal(item)}
                      >
                        <Text style={styles.textForeground}>Reset password</Text>
                      </Button>
                    </View>
                  );
                })}

                {usersQuery.hasNextPage ? (
                  <Button
                    variant="outline"
                    onPress={() => usersQuery.fetchNextPage()}
                    disabled={usersQuery.isFetchingNextPage}
                    style={styles.selfStart}
                  >
                    <Text style={styles.textForeground}>
                      {usersQuery.isFetchingNextPage ? 'Loading...' : 'Load more'}
                    </Text>
                  </Button>
                ) : null}
              </View>
            ) : null}
          </CardContent>
        </Card>

        {/* Automatic Updates Status */}
        <View style={styles.statusCardsRow}>
          <View style={styles.statusCard}>
            <ScoreUpdaterStatus />
          </View>
          <View style={styles.statusCard}>
            <OddsUpdaterStatus />
          </View>
        </View>

        {/* Current Active Week */}
        {currentWeek && (
            <Card style={styles.currentWeekCard}>
              <CardContent style={styles.currentWeekContent}>
                <View style={styles.iconRow}>
                  <CheckCircle size={24} color={colors.text.primary} />
                  <View style={styles.currentWeekTextBlock}>
                    <Text style={styles.currentWeekTitle}>
                    Current Active Week: Week {currentWeek.week}
                  </Text>
                    <Text style={styles.currentWeekSubtitle}>
                    {formatDate(currentWeek.startDate)} - {formatDate(currentWeek.endDate)}
                  </Text>
                  </View>
                </View>
            </CardContent>
          </Card>
        )}

        {/* Active Week Selector */}
        <Card>
          <CardHeader>
            <CardTitle>
              <View style={styles.iconRow}>
                <Calendar size={20} color={colors.text.secondary} />
                <Text style={styles.sectionTitle}>
                  Set Active Week
                </Text>
              </View>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Text style={styles.descriptionText}>
              Choose a week to activate for every league. Members can only make picks for the week
              that is selected below.
            </Text>
            <View style={styles.fieldGroup}>
              <AppSelect
                value={selectedWeekId ?? ''}
                onChange={(value) => setSelectedWeekId(value || null)}
                options={(weeks ?? []).map((week) => ({
                  value: week.id,
                  label: `Week ${week.week}: ${formatDate(week.startDate)} - ${formatDate(week.endDate)}`,
                }))}
                placeholder={currentWeek ? `Week ${currentWeek.week}` : 'Choose a week'}
                triggerStyle={fullWidthTriggerStyle}
              />
              <Button
                onPress={() => selectedWeekId && activateWeekMutation.mutate(selectedWeekId)}
                disabled={!selectedWeekId || !canSetActiveWeek}
                style={styles.primaryActionButton}
              >
                <CheckCircle size={16} color={colors.text.primary} />
                <Text style={styles.inverseActionText}>
                  {activateWeekMutation.isPending
                    ? "Updating..."
                    : isSelectedWeekActive
                      ? "Already Active"
                      : "Set Active Week"}
                </Text>
              </Button>
            </View>

            {selectedWeek && (
              <View
                style={styles.selectedWeekPanel}
              >
                <Text style={styles.selectedWeekTitle}>
                  Week {selectedWeek.week} Details
                </Text>
                <View style={styles.selectedWeekDateRow}>
                  <Text style={styles.selectedWeekDateText}>
                    {formatDate(selectedWeek.startDate)} - {formatDate(selectedWeek.endDate)}
                  </Text>
                  <Badge variant={selectedWeekStatus?.variant ?? 'outline'}>
                    <Text
                      style={
                        selectedWeekStatus?.variant === 'outline'
                          ? styles.badgeStatusTextOutline
                          : styles.badgeStatusTextInverse
                      }
                    >
                      {selectedWeekStatus?.status ?? 'upcoming'}
                    </Text>
                  </Badge>
                </View>
                {isSelectedWeekActive ? (
                  <View style={styles.activeStatusRow}>
                    <Clock size={14} color={colors.success[600]} />
                    <Text style={styles.activeStatusText}>
                      This week is currently active
                    </Text>
                  </View>
                ) : (
                  <Text style={styles.statusFooterText}>
                    Setting this week active will immediately update all leagues.
                  </Text>
                )}
              </View>
            )}
          </CardContent>
        </Card>

        {/* Help Section */}
        <View style={styles.helpPanel}>
          <Text style={styles.helpTitle}>
            How Week Management Works
          </Text>
          <View style={styles.helpList}>
            <Text style={styles.bodyMuted}>
              • The active week determines which games are available for picking
            </Text>
            <Text style={styles.bodyMuted}>
              • Users can only make picks for games in the currently active week
            </Text>
            <Text style={styles.bodyMuted}>
              • Change the active week to advance your league to the next set of games
            </Text>
            <Text style={styles.bodyMuted}>
              • Past weeks are marked as completed, future weeks are marked as upcoming
            </Text>
          </View>
        </View>
        </View>
      </ScrollView>

      <Modal
        visible={resetModalVisible}
        transparent
        animationType="fade"
        onRequestClose={closeResetModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              Reset User Password
            </Text>
            <Text style={styles.modalSubtitle}>
              Set a new password for {resetTarget?.email ?? 'this user'}.
            </Text>

            <View style={styles.modalFields}>
              <Input
                placeholder="New password"
                secureTextEntry
                value={resetPassword}
                onChangeText={setResetPassword}
                editable={!resetPasswordMutation.isPending}
              />
              <Input
                placeholder="Confirm new password"
                secureTextEntry
                value={resetConfirm}
                onChangeText={setResetConfirm}
                editable={!resetPasswordMutation.isPending}
              />
            </View>

            {resetError ? (
              <Text style={styles.modalError}>{resetError}</Text>
            ) : null}

            <View style={styles.modalActions}>
              <Button
                variant="outline"
                onPress={closeResetModal}
                disabled={resetPasswordMutation.isPending}
              >
                <Text style={styles.textForeground}>Cancel</Text>
              </Button>
              <Button
                onPress={handleResetPassword}
                disabled={resetPasswordMutation.isPending}
                style={styles.primaryFillButton}
              >
                <Text style={styles.textInverse}>
                  {resetPasswordMutation.isPending ? 'Updating...' : 'Reset password'}
                </Text>
              </Button>
            </View>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create(() => ({
  actionButtonsWrap: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  activeStatusRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  activeStatusText: {
    color: colors.success[600],
    fontSize: 12,
    fontWeight: '500',
    marginLeft: 6,
  },
  badgeStatusTextInverse: {
    color: colors.text.inverse,
    fontSize: 10,
  },
  badgeStatusTextOutline: {
    color: colors.neutral[700],
    fontSize: 10,
  },
  betweenRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  bodyMuted: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    lineHeight: 20,
  },
  broadcastHelpText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    marginTop: 12,
    textTransform: 'uppercase',
  },
  captionMutedLine: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    lineHeight: 20,
  },
  cardDescriptionCompact: {
    color: colors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 12,
  },
  cardMargin: {
    marginBottom: 16,
  },
  centeredState: {
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    padding: 16,
  },
  compactSection: {
    gap: 12,
    marginBottom: 12,
  },
  counterText: {
    color: colors.text.secondary,
    fontSize: 12,
  },
  currentWeekCard: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 2,
    marginBottom: 16,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  currentWeekContent: {
    paddingTop: 24,
  },
  currentWeekSubtitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    textTransform: 'uppercase',
  },
  currentWeekTextBlock: {
    marginLeft: 12,
  },
  currentWeekTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  descriptionText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 16,
  },
  fieldGroup: {
    gap: 12,
  },
  flexOne: {
    flex: 1,
  },
  helpList: {
    gap: 4,
  },
  helpPanel: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 2,
    marginBottom: 32,
    marginTop: 16,
    padding: 16,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 4, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 0,
  },
  helpTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 16,
    fontWeight: '900',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  iconRow: {
    alignItems: 'center',
    flexDirection: 'row',
  },
  inlineActionWrap: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  inlineEndRow: {
    alignItems: 'flex-end',
    flexDirection: 'row',
    gap: 12,
  },
  inverseActionText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    marginLeft: 8,
    textTransform: 'uppercase',
  },
  labelCounterRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  loadingSkeletonCard: {
    backgroundColor: colors.ink.paperDark,
    borderRadius: 0,
    height: 80,
    marginBottom: 12,
    opacity: 0.6,
  },
  metaRow: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  minActionButton: {
    alignSelf: 'flex-start',
    minWidth: 160,
  },
  minAmberActionButton: {
    alignSelf: 'flex-start',
    backgroundColor: colors.warning[100],
    borderColor: colors.ink.press,
    borderWidth: 2,
    minWidth: 160,
  },
  minCyanActionButton: {
    alignSelf: 'flex-start',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    minWidth: 160,
  },
  minPrimaryActionButton: {
    alignSelf: 'flex-start',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    minWidth: 160,
  },
  minVioletActionButton: {
    alignSelf: 'flex-start',
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderWidth: 2,
    minWidth: 160,
  },
  modalActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 12,
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  modalCard: {
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 3,
    elevation: 6,
    maxWidth: 420,
    padding: 24,
    shadowColor: colors.ink.press,
    shadowOffset: { width: 14, height: 14 },
    shadowOpacity: 1,
    shadowRadius: 0,
    width: '100%',
  },
  modalError: {
    color: colors.error[400],
    fontSize: 14,
    marginTop: 12,
  },
  modalFields: {
    gap: 12,
  },
  modalOverlay: {
    alignItems: 'center',
    backgroundColor: modalOverlayColor,
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  modalSubtitle: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.body,
    fontSize: 14,
    marginBottom: 16,
  },
  modalTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    marginBottom: 8,
    textTransform: 'uppercase',
  },
  pageContainer: {
    alignSelf: 'center',
    gap: 24,
    maxWidth: 1040,
    width: '100%',
  },
  pageTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 28,
    fontWeight: '900',
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  panelCard: {
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    gap: 12,
    marginBottom: 16,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  panelCardWithTopMargin: {
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    gap: 12,
    marginTop: 16,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  primaryActionButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  primaryBlueButton: {
    alignSelf: 'flex-start',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
    marginTop: 16,
  },
  primaryFillButton: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.press,
    borderWidth: 2,
  },
  restrictedCopy: {
    alignItems: 'center',
    maxWidth: 300,
  },
  restrictedSubtitle: {
    color: colors.text.secondary,
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 24,
    textAlign: 'center',
  },
  restrictedTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 8,
    textAlign: 'center',
  },
  rowGap: {
    flexDirection: 'row',
    gap: 12,
  },
  scheduleActionWrap: {
    alignItems: 'stretch',
    gap: 8,
  },
  scheduleRefreshButton: {
    alignSelf: 'stretch',
  },
  screenPadding: {
    padding: 16,
  },
  screenRoot: {
    backgroundColor: colors.background,
    flex: 1,
  },
  screenScrollContent: {
    paddingHorizontal: 16,
    paddingVertical: 32,
  },
  screenSurface: {
    backgroundColor: colors.background,
    flex: 1,
  },
  sectionGap: {
    gap: 16,
  },
  sectionLabel: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  sectionTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 18,
    fontWeight: '900',
    marginLeft: 8,
    textTransform: 'uppercase',
  },
  selectedWeekDateRow: {
    alignItems: 'center',
    flexDirection: 'row',
    marginBottom: 8,
  },
  selectedWeekDateText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 14,
    marginRight: 8,
  },
  selectedWeekPanel: {
    backgroundColor: colors.ink.paperDark,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    marginTop: 16,
    padding: 12,
  },
  selectedWeekTitle: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: 14,
    fontWeight: '900',
    marginBottom: 8,
  },
  selfStart: {
    alignSelf: 'flex-start',
  },
  smallMutedText: {
    color: colors.text.secondary,
    fontSize: 14,
  },
  statusCard: {
    flex: 1,
    minWidth: 320,
  },
  statusCardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  statusFooterText: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
  },
  textForeground: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  textInverse: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  topHeaderRow: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: 16,
    justifyContent: 'space-between',
  },
  uppercaseFieldLabel: {
    color: colors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    marginBottom: 4,
    textTransform: 'uppercase',
  },
  uppercaseFieldLabelTight: {
    color: colors.text.secondary,
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  userList: {
    gap: 12,
  },
  userListCount: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  userNameText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.bodyMedium,
    fontSize: 14,
    fontWeight: '600',
  },
  userRow: {
    alignItems: 'center',
    backgroundColor: colors.ink.paper,
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'space-between',
    padding: 12,
  },
  userSearchInputWrap: {
    flex: 1,
    minWidth: 200,
  },
  userSearchRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  warningPanel: {
    backgroundColor: colors.warning[50],
    borderColor: colors.ink.press,
    borderRadius: 0,
    borderWidth: 2,
    gap: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  warningText: {
    color: colors.warning[900],
    fontSize: 12,
  },
  warningTitle: {
    color: colors.warning[900],
    fontFamily: typography.fontFamily.mono,
    fontSize: 12,
    fontWeight: '600',
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  width112: {
    width: 112,
  },
  width160: {
    width: 160,
  },
}));
